/**
 * YouTube Streaming Service - Web Implementation
 *
 * Implements YouTube Live Chat API integration using HTTP polling.
 * Ported from beemi-react-native for web compatibility.
 */

import { StreamingPlatformService } from '../base/StreamingPlatformService';
import {
  PlatformConnectionOptions,
  StreamValidationResult,
  StreamingComment,
  StreamingMetrics,
  StreamingRoomInfo
} from '../../../types/streaming';
import { streamingConfigService } from '../StreamingConfigService';

const YOUTUBE_API_BASE_URL = 'https://www.googleapis.com/youtube/v3';

// YouTube-specific types
export interface YouTubeChannelInfo {
  id: string;
  handle: string;
  name: string;
  imageUrl?: string;
  uploadPlaylistId: string;
  subscriberCount?: number;
}

export interface YouTubeStreamInfo {
  videoId: string;
  url: string;
  startTime: Date;
  status: 'live' | 'upcoming' | 'ended';
  liveChatId?: string;
  title?: string;
  thumbnailUrl?: string;
}

export interface YouTubeLiveChatMessage {
  id: string;
  snippet: {
    type: string;
    authorChannelId: string;
    publishedAt: string;
    displayMessage: string;
  };
  authorDetails: {
    channelId: string;
    displayName: string;
    profileImageUrl: string;
    channelUrl: string;
  };
}

export interface YouTubeLiveChatResponse {
  items: YouTubeLiveChatMessage[];
  nextPageToken?: string;
  pollingIntervalMillis: number;
  pageInfo: {
    totalResults: number;
    resultsPerPage: number;
  };
}

export interface YouTubeVideoInfo {
  id: string;
  snippet: {
    title: string;
    channelId: string;
    liveBroadcastContent: string;
  };
  liveStreamingDetails?: {
    activeLiveChatId?: string;
    concurrentViewers?: number;
    actualStartTime?: string;
    scheduledStartTime?: string;
  };
  statistics?: {
    likeCount?: string;
    viewCount?: string;
  };
}

export class YouTubeStreamingService extends StreamingPlatformService {
  private apiKey: string | null = null;
  private channelInfo: YouTubeChannelInfo | null = null;
  private streamInfo: YouTubeStreamInfo | null = null;
  private pollingInterval: ReturnType<typeof setTimeout> | null = null;
  private viewerCountInterval: ReturnType<typeof setInterval> | null = null;
  private nextPageToken: string | null = null;
  private pollingIntervalMs = 3000;
  private isPolling = false;
  private currentUsername = '';
  private processedMessageIds = new Set<string>();

  constructor() {
    super('youtube');
  }

  /**
   * Get current stream information
   */
  getStreamInfo(): YouTubeStreamInfo | null {
    return this.streamInfo;
  }

  /**
   * Get current channel information
   */
  getCurrentChannelInfo(): YouTubeChannelInfo | null {
    return this.channelInfo;
  }

  async connect(options: PlatformConnectionOptions): Promise<void> {
    console.log('📺 [YouTube] Connecting with username:', options.username);
    console.log('📺 [YouTube] Options received:', {
      hasUsername: !!options.username,
      hasOnEvent: !!options.onEvent,
      hasOnMetricsUpdate: !!options.onMetricsUpdate,
      hasOnConnect: !!options.onConnect,
      onEventType: typeof options.onEvent
    });
    
    if (this.isCleanedUp) {
      throw new Error('Service has been cleaned up');
    }

    this.validateConnectionOptions(options);
    this.currentUsername = options.username;
    
    // Store callbacks - explicitly check and log each one
    const onEventCallback = options.onEvent;
    console.log('📺 [YouTube] onEvent before storing:', typeof onEventCallback, onEventCallback);
    
    this.callbacks = {
      onConnect: options.onConnect,
      onDisconnect: options.onDisconnect,
      onError: options.onError,
      onComment: options.onComment,
      onConnectionStatus: options.onConnectionStatus,
      onMetricsUpdate: options.onMetricsUpdate,
      onRoomInfo: options.onRoomInfo,
      onEvent: onEventCallback  // Store onEvent callback for stats and other raw events
    };
    
    console.log('📺 [YouTube] Callbacks stored:', {
      hasOnConnect: !!this.callbacks.onConnect,
      hasOnEvent: !!this.callbacks.onEvent,
      hasOnMetricsUpdate: !!this.callbacks.onMetricsUpdate,
      onEventType: typeof this.callbacks.onEvent
    });

    try {
      this.setConnectionState('connecting');

      // Get API key from backend
      await this.getYouTubeApiKey();

      if (!this.apiKey) {
        throw this.createPlatformError('Failed to obtain YouTube API key');
      }

      // Get channel info
      console.log('📺 [YouTube] Getting channel info for:', this.currentUsername);
      this.channelInfo = await this.getChannelInfo(this.currentUsername);

      if (!this.channelInfo) {
        throw this.createPlatformError(`No YouTube channel found for '${this.currentUsername}'`);
      }

      // Find active live stream
      console.log('📺 [YouTube] Finding active live stream...');
      this.streamInfo = await this.findActiveStream(this.channelInfo);

      if (!this.streamInfo) {
        throw this.createPlatformError(`No active live stream found for '${this.currentUsername}'`);
      }

      // Get live chat ID
      if (!this.streamInfo.liveChatId) {
        await this.getLiveChatId(this.streamInfo.videoId);
      }

      if (!this.streamInfo.liveChatId) {
        throw this.createPlatformError('Live chat is not available for this stream');
      }

      // Setup room info
      this.setupRoomInfo();

      // Start polling
      this.startPolling();
      this.startViewerCountPolling();

      this.setConnectionState('connected');
      console.log('📺 [YouTube] ✅ Connected successfully!');

    } catch (error) {
      this.handleError(error as Error, 'Connection failed');
      throw error;
    }
  }

  disconnect(): void {
    this.stopPolling();
    this.stopViewerCountPolling();

    this.channelInfo = null;
    this.streamInfo = null;
    this.nextPageToken = null;
    this.apiKey = null;
    this.processedMessageIds.clear();

    this.setConnectionState('disconnected');
  }

  validateStreamRequirements(): StreamValidationResult {
    return {
      isLive: this.isConnected() && this.streamInfo?.status === 'live',
      hasActiveChat: this.streamInfo?.liveChatId != null,
      platform: 'youtube'
    };
  }

  private async getYouTubeApiKey(): Promise<void> {
    const config = await streamingConfigService.getConfig();
    
    if (!config.youtube.enabled || !config.youtube.apiKey) {
      throw new Error('YouTube API is not configured');
    }

    this.apiKey = config.youtube.apiKey;
  }

  private async getChannelInfo(username: string): Promise<YouTubeChannelInfo | null> {
    const cleanUsername = username.startsWith('@') ? username.slice(1) : username;

    // Try by handle first
    let url = `${YOUTUBE_API_BASE_URL}/channels?part=id,contentDetails,snippet,statistics&forHandle=${cleanUsername}&key=${this.apiKey}`;
    let response = await this.makeApiRequest<{items: any[]}>(url);

    // If not found, try by username
    if (!response.items || response.items.length === 0) {
      url = `${YOUTUBE_API_BASE_URL}/channels?part=id,contentDetails,snippet,statistics&forUsername=${cleanUsername}&key=${this.apiKey}`;
      response = await this.makeApiRequest<{items: any[]}>(url);
    }

    if (!response.items || response.items.length === 0) {
      return null;
    }

    const channel = response.items[0];
    const subscriberCount = channel.statistics?.subscriberCount ? parseInt(channel.statistics.subscriberCount, 10) : 0;
    
    return {
      id: channel.id,
      handle: channel.snippet?.customUrl || cleanUsername,
      name: channel.snippet?.title || 'Unknown',
      imageUrl: channel.snippet?.thumbnails?.default?.url,
      uploadPlaylistId: channel.contentDetails?.relatedPlaylists?.uploads,
      subscriberCount: subscriberCount
    };
  }

  private async findActiveStream(channelInfo: YouTubeChannelInfo): Promise<YouTubeStreamInfo | null> {
    if (!channelInfo.uploadPlaylistId) {
      return null;
    }

    // Get recent videos
    const playlistUrl = `${YOUTUBE_API_BASE_URL}/playlistItems?part=contentDetails,snippet&playlistId=${channelInfo.uploadPlaylistId}&key=${this.apiKey}&maxResults=25`;
    const playlistResponse = await this.makeApiRequest<{items: any[]}>(playlistUrl);

    if (!playlistResponse.items || playlistResponse.items.length === 0) {
      return null;
    }

    // Find live streams
    const liveStreams = playlistResponse.items
      .filter(item => item.snippet?.thumbnails?.default?.url?.includes('_live.'))
      .sort((a, b) => new Date(b.contentDetails.videoPublishedAt).getTime() - new Date(a.contentDetails.videoPublishedAt).getTime());

    if (liveStreams.length === 0) {
      return null;
    }

    const videoIds = liveStreams.map(item => item.contentDetails.videoId).join(',');

    // Get detailed video info
    const videosUrl = `${YOUTUBE_API_BASE_URL}/videos?part=snippet,liveStreamingDetails&id=${videoIds}&key=${this.apiKey}`;
    const videosResponse = await this.makeApiRequest<{items: YouTubeVideoInfo[]}>(videosUrl);

    if (!videosResponse.items || videosResponse.items.length === 0) {
      return null;
    }

    // Find first live video
    const liveVideo = videosResponse.items.find(video =>
      video.snippet.liveBroadcastContent === 'live' &&
      video.liveStreamingDetails?.activeLiveChatId
    );

    if (!liveVideo) {
      return null;
    }

    const startTime = liveVideo.liveStreamingDetails?.actualStartTime ||
                     liveVideo.liveStreamingDetails?.scheduledStartTime ||
                     new Date().toISOString();

    return {
      videoId: liveVideo.id,
      url: `https://www.youtube.com/watch?v=${liveVideo.id}`,
      startTime: new Date(startTime),
      status: 'live',
      liveChatId: liveVideo.liveStreamingDetails?.activeLiveChatId,
      title: liveVideo.snippet?.title,
      thumbnailUrl: `https://i.ytimg.com/vi/${liveVideo.id}/maxresdefault.jpg`
    };
  }

  private async getLiveChatId(videoId: string): Promise<void> {
    const url = `${YOUTUBE_API_BASE_URL}/videos?part=liveStreamingDetails&id=${videoId}&key=${this.apiKey}`;
    const response = await this.makeApiRequest<{items: YouTubeVideoInfo[]}>(url);

    if (response.items && response.items.length > 0) {
      const liveChatId = response.items[0].liveStreamingDetails?.activeLiveChatId;
      if (liveChatId && this.streamInfo) {
        this.streamInfo.liveChatId = liveChatId;
      }
    }
  }

  private setupRoomInfo(): void {
    if (!this.channelInfo || !this.streamInfo) return;

    const roomInfo: StreamingRoomInfo = {
      platform: 'youtube',
      isLive: this.streamInfo.status === 'live',
      roomId: this.streamInfo.videoId,
      channelId: this.channelInfo.handle || this.currentUsername,
      title: this.streamInfo.title || this.channelInfo.name,
      streamImageUrl: this.streamInfo.thumbnailUrl,
      streamerInfo: {
        uniqueId: this.channelInfo.id,
        nickname: this.channelInfo.name,
        userId: this.channelInfo.id,
        profileImageUrl: this.channelInfo.imageUrl,
        followerCount: this.channelInfo.subscriberCount
      }
    };

    console.log('📺 [YouTube] Room info setup:', {
      title: roomInfo.title,
      streamImageUrl: roomInfo.streamImageUrl,
      profileImageUrl: roomInfo.streamerInfo?.profileImageUrl
    });

    this.handleRoomInfo(roomInfo);
  }

  private startPolling(): void {
    if (this.isPolling || !this.streamInfo?.liveChatId) return;

    this.isPolling = true;
    this.pollMessages();
  }

  private async pollMessages(): Promise<void> {
    if (!this.isPolling || !this.streamInfo?.liveChatId || this.isCleanedUp) {
      return;
    }

    try {
      let url = `${YOUTUBE_API_BASE_URL}/liveChat/messages?liveChatId=${this.streamInfo.liveChatId}&part=snippet,authorDetails&key=${this.apiKey}&maxResults=200`;

      if (this.nextPageToken) {
        url += `&pageToken=${this.nextPageToken}`;
      }

      const response = await this.makeApiRequest<YouTubeLiveChatResponse>(url);

      if (response.items) {
        for (const message of response.items) {
          this.processMessage(message);
        }

        this.updateMetrics({
          commentCount: (this.streamMetrics.commentCount || 0) + response.items.length
        });
      }

      this.nextPageToken = response.nextPageToken || null;
      this.pollingIntervalMs = response.pollingIntervalMillis || 3000;

    } catch (error) {
      console.warn('YouTube polling error:', error);
    }

    // Schedule next poll
    if (this.isPolling && !this.isCleanedUp) {
      this.pollingInterval = setTimeout(() => {
        this.pollMessages();
      }, this.pollingIntervalMs);
    }
  }

  private processMessage(message: YouTubeLiveChatMessage): void {
    if (message.snippet.type !== 'textMessageEvent') {
      return;
    }

    // Duplicate prevention
    if (this.processedMessageIds.has(message.id)) {
      return;
    }
    this.processedMessageIds.add(message.id);

    const comment: StreamingComment = {
      id: message.id,
      username: message.authorDetails.displayName,
      comment: message.snippet.displayMessage,
      timestamp: new Date(message.snippet.publishedAt).getTime(),
      platform: 'youtube',
      imageUrl: message.authorDetails.profileImageUrl
    };

    this.handleComment(comment);
  }

  private startViewerCountPolling(): void {
    if (this.viewerCountInterval || !this.streamInfo) return;

    const pollVideoData = async () => {
      if (!this.streamInfo?.videoId || this.isCleanedUp) return;

      try {
        const url = `${YOUTUBE_API_BASE_URL}/videos?part=liveStreamingDetails,statistics&id=${this.streamInfo.videoId}&key=${this.apiKey}`;
        const response = await this.makeApiRequest<{items: YouTubeVideoInfo[]}>(url);

        if (response.items && response.items.length > 0) {
          const videoData = response.items[0];
          const liveStreamingDetails = videoData.liveStreamingDetails;
          const statistics = videoData.statistics;
          
          const viewerCount = liveStreamingDetails?.concurrentViewers || 0;
          const likeCount = statistics?.likeCount || 0;
          
          this.updateMetrics({ 
            viewerCount: typeof viewerCount === 'string' ? parseInt(viewerCount) : viewerCount,
            likeCount: typeof likeCount === 'string' ? parseInt(likeCount) : likeCount
          });

          // Send stats event to game (like TikTok does)
          const statsEvent = {
            type: 'stats',
            data: {
              platform: 'youtube',
              viewerCount: typeof viewerCount === 'string' ? parseInt(viewerCount) : viewerCount,
              likeCount: typeof likeCount === 'string' ? parseInt(likeCount) : likeCount,
              commentCount: this.streamMetrics.commentCount || 0,
              giftCount: this.streamMetrics.giftCount || 0,
              shareCount: this.streamMetrics.shareCount || 0,
              memberCount: this.streamMetrics.memberCount || 0
            },
            platform: 'youtube'
          };
          console.log('📺 [YouTube] Sending stats event:', statsEvent, 'hasOnEvent:', !!this.callbacks.onEvent);
          if (this.callbacks.onEvent) {
            this.callbacks.onEvent(statsEvent);
          } else {
            console.warn('📺 [YouTube] ⚠️ onEvent callback is undefined! Stats NOT sent to game.');
          }
        }
      } catch (error) {
        console.warn('YouTube video data polling error:', error);
      }
    };

    // Poll every 10 seconds
    this.viewerCountInterval = setInterval(pollVideoData, 10000);

    // Get initial data
    pollVideoData();
  }

  private stopPolling(): void {
    this.isPolling = false;

    if (this.pollingInterval) {
      clearTimeout(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private stopViewerCountPolling(): void {
    if (this.viewerCountInterval) {
      clearInterval(this.viewerCountInterval);
      this.viewerCountInterval = null;
    }
  }

  private async makeApiRequest<T>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`YouTube API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  protected async attemptReconnect(): Promise<void> {
    if (this.currentUsername) {
      const options: PlatformConnectionOptions = {
        platform: 'youtube',
        username: this.currentUsername
      };
      return this.connect(options);
    }
    throw new Error('No connection options available for reconnection');
  }

  public cleanup(): void {
    this.stopPolling();
    this.stopViewerCountPolling();
    this.processedMessageIds.clear();
    super.cleanup();
  }
}

// Singleton instance
export const youTubeStreamingService = new YouTubeStreamingService();

